import { Account } from '@near-js/accounts';
import { PublicKey } from '@near-js/crypto';
import { BrowserLocalStorageKeyStore } from '@near-js/keystores-browser';
import { JsonRpcProvider } from '@near-js/providers';
import { InMemorySigner, KeyPairSigner } from '@near-js/signers';
import { actionCreators } from '@near-js/transactions';
import { NearConnector } from '@hot-labs/near-connect';
import BN from 'bn.js';

import { decorateWithLockup } from './account-with-lockup';

import config from '../config';
const {
  ACCOUNT_ID_SUFFIX,
  NODE_URL,
  OPN_CONTRACT,
  OCC_CONTRACT,
  NFT_CONTRACT,
  USDC_CONTRACT,
  COLLATERAL_CONTRACT
} = config;

const DAPP_CONTRACT_IDS = [
  OPN_CONTRACT,
  OCC_CONTRACT,
  NFT_CONTRACT,
  USDC_CONTRACT,
  COLLATERAL_CONTRACT
].filter(Boolean);

const ACCESS_KEYS_PAGE_LIMIT = 50;

const networkId = ACCOUNT_ID_SUFFIX as 'mainnet' | 'testnet';
const METEOR_WALLET_ID = 'meteor-wallet';
const SESSION_KEY_STORAGE_KEY = 'ofp:near-connect-session-key-by-account';

const METEOR_WALLET_MANIFEST = {
  version: '1.1.0',
  wallets: [
    {
      id: METEOR_WALLET_ID,
      name: 'Meteor Wallet',
      icon: 'https://raw.githubusercontent.com/Meteor-Wallet/meteor_wallet_sdk/main/assets/meteor-logo-svg.svg',
      description:
        'The most simple and secure wallet to manage your crypto, access DeFi, and explore Web3',
      website: 'https://meteorwallet.app/',
      version: '1.1.0',
      executor:
        'https://raw.githubusercontent.com/Meteor-Wallet/meteor_wallet_sdk/data-storage/storage/meteor-near-connect-latest.js',
      type: 'sandbox',
      features: {
        signMessage: true,
        signInWithoutAddKey: true,
        signInAndSignMessage: true,
        signAndSendTransaction: true,
        signAndSendTransactions: true,
        signInWithFunctionCallKey: true,
        signDelegateActions: true,
        mainnet: true,
        testnet: true
      },
      permissions: {
        storage: true,
        allowsOpen: [
          'https://chromewebstore.google.com',
          'https://wallet.meteorwallet.app',
          'https://meteorwallet.app'
        ],
        external: ['meteorCom', 'meteorComV2']
      },
      platform: {
        web: 'https://wallet.meteorwallet.app',
        chrome:
          'https://chromewebstore.google.com/detail/meteor-wallet/pcndjhkinnkaohffealmlmhaepkpmgkb'
      }
    }
  ]
};

const ACCOUNT_ID_REGEX =
  /^(([a-z\d]+[-_])*[a-z\d]+\.)*([a-z\d]+[-_])*[a-z\d]+$/;

type NearConnection = {
  connection: {
    networkId: string;
    provider: JsonRpcProvider;
    signer: InMemorySigner;
  };
  account: (accountId: string) => Promise<Account>;
};

type SignAndSendTransactionOptions = {
  receiverId: string;
  actions: any[];
};

export type WalletTransaction = {
  receiverId: string;
  actions: ReturnType<typeof actionCreators.functionCall>[];
};

export const createFunctionCallAction = (
  methodName: string,
  args: object = {},
  gas: string | BN | number | bigint = '30000000000000',
  deposit: string | BN | number | bigint = '0'
) =>
  actionCreators.functionCall(
    methodName,
    args,
    BigInt(gas.toString()),
    BigInt(deposit.toString())
  );

const toBigInt = (value: BN | string | number | bigint | undefined | null) => {
  if (value === undefined || value === null) return BigInt(0);
  if (typeof value === 'bigint') return value;
  return BigInt(value.toString());
};

const decodeFunctionCallArgs = (args: Uint8Array | object) => {
  if (!args) return {};
  if (!(args instanceof Uint8Array)) return args;
  try {
    const decoded = Buffer.from(args).toString();
    return decoded ? JSON.parse(decoded) : {};
  } catch (e) {
    return args;
  }
};

const convertNearActionToSelectorAction = (action: any) => {
  // Already a near-connect / @near-js transactions Action — keep as-is.
  if (
    action?.enum === 'functionCall' ||
    action?.enum === 'transfer' ||
    action?.enum === 'deleteKey' ||
    action?.type === 'FunctionCall' ||
    action?.type === 'Transfer' ||
    action?.type === 'DeleteKey'
  ) {
    if (action?.enum === 'functionCall' && action?.functionCall) {
      return actionCreators.functionCall(
        action.functionCall.methodName,
        decodeFunctionCallArgs(action.functionCall.args),
        toBigInt(action.functionCall.gas),
        toBigInt(action.functionCall.deposit)
      );
    }
    if (action?.enum === 'transfer' && action?.transfer) {
      return actionCreators.transfer(toBigInt(action.transfer.deposit));
    }
    if (action?.enum === 'deleteKey' && action?.deleteKey) {
      return actionCreators.deleteKey(action.deleteKey.publicKey);
    }
    if (action?.type === 'FunctionCall') {
      return actionCreators.functionCall(
        action.params?.methodName || action.methodName,
        action.params?.args || action.args || {},
        toBigInt(action.params?.gas || action.gas || '30000000000000'),
        toBigInt(action.params?.deposit || action.deposit || '0')
      );
    }
    if (action?.type === 'Transfer') {
      return actionCreators.transfer(
        toBigInt(action?.params?.deposit || action?.deposit)
      );
    }
    if (action?.type === 'DeleteKey') {
      const publicKey =
        action?.params?.publicKey || action?.publicKey || action?.public_key;
      return actionCreators.deleteKey(PublicKey.fromString(publicKey));
    }
  }

  const functionCall =
    action?.functionCall ||
    (action?.enum === 'functionCall' ? action.functionCall : null);
  if (functionCall) {
    return actionCreators.functionCall(
      functionCall.methodName,
      decodeFunctionCallArgs(functionCall.args),
      toBigInt(functionCall.gas),
      toBigInt(functionCall.deposit)
    );
  }

  const transfer =
    action?.transfer || (action?.enum === 'transfer' ? action.transfer : null);
  if (transfer) {
    return actionCreators.transfer(toBigInt(transfer.deposit));
  }

  const deleteKey =
    action?.deleteKey || (action?.enum === 'deleteKey' ? action.deleteKey : null);
  if (deleteKey?.publicKey) {
    return actionCreators.deleteKey(deleteKey.publicKey);
  }

  throw new Error('Unsupported transaction action for Meteor wallet');
};

const getErrorMessage = (error: any) => {
  const toStringValue =
    typeof error?.toString === 'function' ? error.toString() : '';
  return [
    error?.message,
    error?.cause?.message,
    error?.error?.message,
    error?.status?.Failure,
    toStringValue !== '[object Object]' ? toStringValue : ''
  ]
    .filter(Boolean)
    .join(' | ');
};

const isTooManyAccessKeysError = (error: any) => {
  const text = getErrorMessage(error).toLowerCase();
  return (
    text.includes('more than') &&
    text.includes('access key') &&
    text.includes('paginated view_access_key_list request')
  );
};

const isIframeNotLoadedError = (error: any) =>
  getErrorMessage(error).toLowerCase().includes('iframe not loaded');

const normalizePublicKey = (publicKey: string) => {
  if (!publicKey || typeof publicKey !== 'string') return '';
  return publicKey.startsWith('ed25519:') ? publicKey : `ed25519:${publicKey}`;
};

const readSessionKeyByAccount = (): Record<string, string> => {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY_STORAGE_KEY) || '{}');
  } catch (e) {
    return {};
  }
};

const writeSessionKeyByAccount = (value: Record<string, string>) => {
  try {
    localStorage.setItem(SESSION_KEY_STORAGE_KEY, JSON.stringify(value || {}));
  } catch (e) {
    // ignore
  }
};

const rememberSessionKeyByAccount = (
  accountId: string,
  publicKeyRaw: string
) => {
  const publicKey = normalizePublicKey(publicKeyRaw || '');
  if (!accountId || !publicKey) return;
  const current = readSessionKeyByAccount();
  current[accountId] = publicKey;
  writeSessionKeyByAccount(current);
};

const getRememberedSessionKey = (accountId: string) => {
  if (!accountId) return '';
  return readSessionKeyByAccount()[accountId] || '';
};

const forgetSessionKey = (accountId: string) => {
  if (!accountId) return;
  const current = readSessionKeyByAccount();
  if (!current[accountId]) return;
  delete current[accountId];
  writeSessionKeyByAccount(current);
};

const resolveSessionKeyFromLocalStorage = (accountId: string) => {
  if (!accountId) return '';
  try {
    const keyRegex = /ed25519:[1-9A-HJ-NP-Za-km-z]{30,}/g;
    const candidates: string[] = [];
    Object.keys(localStorage || {}).forEach((storageKey) => {
      const value = localStorage.getItem(storageKey) || '';
      const source = `${storageKey}\n${value}`;
      const hasAccountHint =
        source.includes(accountId) ||
        storageKey.includes('near-api-js:keystore') ||
        storageKey.includes('meteor') ||
        storageKey.includes('wallet');
      if (!hasAccountHint) return;
      const matches = source.match(keyRegex) || [];
      matches.forEach((match) => {
        const normalized = normalizePublicKey(match);
        if (normalized && !candidates.includes(normalized)) {
          candidates.push(normalized);
        }
      });
    });
    return candidates[0] || '';
  } catch (e) {
    return '';
  }
};

const getKeyNonce = (keyInfo: any) => Number(keyInfo?.access_key?.nonce || 0);

const getFunctionCallPermission = (keyInfo: any) => {
  const permission = keyInfo?.access_key?.permission;
  if (!permission || typeof permission === 'string') return null;
  return permission?.FunctionCall || permission?.function_call || null;
};

const getDappFunctionCallKeys = (keys: any[] = []) =>
  keys.filter((keyInfo) => {
    const functionCallPermission = getFunctionCallPermission(keyInfo);
    if (!functionCallPermission) return false;
    const receiverId =
      functionCallPermission?.receiver_id ||
      functionCallPermission?.receiverId ||
      '';
    return DAPP_CONTRACT_IDS.includes(receiverId);
  });

const getAllFunctionCallKeys = (keys: any[] = []) =>
  keys.filter((keyInfo) => !!getFunctionCallPermission(keyInfo));

const readAccessKeysPage = async (
  accountId: string,
  afterKey: string | null = null,
  limit = ACCESS_KEYS_PAGE_LIMIT
) => {
  const response = await fetch(NODE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 'view-access-key-list',
      method: 'query',
      params: {
        request_type: 'view_access_key_list',
        finality: 'final',
        account_id: accountId,
        after_key: afterKey,
        limit
      }
    })
  });
  const data = await response.json();
  if (data?.error) {
    throw new Error(
      data?.error?.message || 'Failed to read account access keys'
    );
  }
  return {
    keys: data?.result?.keys || [],
    lastKey: data?.result?.last_key || null
  };
};

const listAccessKeysPaginated = async (
  accountId: string,
  limit = ACCESS_KEYS_PAGE_LIMIT
) => {
  if (!accountId) return [];
  const keys: any[] = [];
  let afterKey: string | null = null;
  let safetyCounter = 0;

  do {
    const page = await readAccessKeysPage(accountId, afterKey, limit);
    keys.push(...(page.keys || []));
    afterKey = page.lastKey || null;
    safetyCounter += 1;
  } while (afterKey && safetyCounter < 1000);

  return keys;
};

const resolveLikelySessionKeyFromChain = async (accountId: string) => {
  if (!accountId) return '';
  try {
    const allKeys = await listAccessKeysPaginated(accountId);
    const dappKeys = getDappFunctionCallKeys(allKeys);
    const functionCallKeys = getAllFunctionCallKeys(allKeys);
    const byMaxNonce = (list: any[] = []) =>
      list.reduce(
        (best, current) =>
          getKeyNonce(current) > getKeyNonce(best) ? current : best,
        list[0]
      );

    // Never fall back to a full-access key. That is the key Meteor uses to sign.
    const bestDappKey = dappKeys.length ? byMaxNonce(dappKeys) : null;
    const bestFunctionCallKey = functionCallKeys.length
      ? byMaxNonce(functionCallKeys)
      : null;
    const bestKey = bestDappKey || bestFunctionCallKey;
    return normalizePublicKey(
      bestKey?.public_key || bestKey?.publicKey || ''
    );
  } catch (e) {
    return '';
  }
};

const hasLocalSigningKey = async (accountLike: any, accountId: string) => {
  if (!accountLike?.connection?.signer || !accountId) return false;
  try {
    const publicKey = await accountLike.connection.signer.getPublicKey(
      accountId,
      networkId
    );
    return !!publicKey;
  } catch (e) {
    return false;
  }
};

export class WalletCancelError extends Error {
  constructor(message = 'User closed the window') {
    super(message);
    this.name = 'WalletCancelError';
  }
}

const stringifySafe = (value: any) => {
  try {
    return JSON.stringify(value ?? null);
  } catch (e) {
    return String(value);
  }
};

export const inspectWalletError = (error: any) => {
  if (error == null) return null;
  const reason = error?._reason || error?.reason || null;
  const endTags =
    error?.endTags || reason?.endTags || error?.cause?.endTags || null;

  return {
    name: error?.name,
    message: error?.message,
    type: error?.type,
    code: error?.code,
    endTags,
    reason,
    cause: error?.cause,
    failure: error?.failure,
    ownKeys: Object.getOwnPropertyNames(error || {}),
    reasonKeys: reason ? Object.getOwnPropertyNames(reason) : [],
    raw: stringifySafe({
      name: error?.name,
      message: error?.message,
      type: error?.type,
      endTags,
      reason,
      causeMessage: error?.cause?.message,
      kind: error?.kind,
      status: error?.status
    })
  };
};

export const extractTransactionFailure = (value: any): any => {
  if (value == null) return null;

  const visited = new Set<any>();
  const queue: any[] = [value];

  while (queue.length) {
    const current = queue.shift();
    if (current == null || visited.has(current)) continue;
    if (typeof current === 'object') visited.add(current);

    if (typeof current === 'string') {
      const trimmed = current.trim();
      if (
        trimmed.includes('FunctionCallError') ||
        trimmed.includes('ExecutionError') ||
        trimmed.includes('Smart contract panicked') ||
        trimmed.includes('"ActionError"')
      ) {
        try {
          return JSON.parse(trimmed);
        } catch (e) {
          const jsonMatch = trimmed.match(/\{.*FunctionCallError.*\}/);
          if (jsonMatch) {
            try {
              return JSON.parse(jsonMatch[0]);
            } catch (err) {
              return trimmed;
            }
          }
          return trimmed;
        }
      }
      continue;
    }

    if (typeof current !== 'object') continue;

    if (current?.kind?.FunctionCallError || current?.FunctionCallError) {
      return current;
    }
    if (current?.kind?.kind?.FunctionCallError) {
      return current;
    }
    if (current?.status?.Failure) {
      return current.status.Failure;
    }

    [
      'Failure',
      'ActionError',
      'kind',
      'error',
      '_reason',
      'reason',
      'endTags',
      'cause',
      'data',
      'payload',
      'message',
      'body',
      'response',
      'failure'
    ].forEach((key) => {
      if (current[key] != null) queue.push(current[key]);
    });

    if (Array.isArray(current)) {
      current.forEach((item) => queue.push(item));
    } else {
      Object.values(current).forEach((item) => {
        if (item && (typeof item === 'object' || typeof item === 'string')) {
          queue.push(item);
        }
      });
    }
  }

  return null;
};

export const extractTransactionErrorMessage = (error: any): string => {
  const failure = extractTransactionFailure(error);
  if (failure) {
    const executionError =
      failure?.kind?.FunctionCallError?.ExecutionError ||
      failure?.FunctionCallError?.ExecutionError ||
      failure?.kind?.kind?.FunctionCallError?.ExecutionError;
    if (typeof executionError === 'string' && executionError) {
      return executionError;
    }
    return stringifySafe(failure);
  }

  const inspected = inspectWalletError(error);
  return (
    inspected?.message ||
    error?.message ||
    error?.cause?.message ||
    error?.error?.message ||
    stringifySafe(error) ||
    'Unknown transaction error'
  );
};

const captureMeteorMessages = () => {
  const messages: any[] = [];
  let lastFailure: any = null;

  const onData = (data: any) => {
    if (data == null) return;
    messages.push(data);
    const failure = extractTransactionFailure(data);
    if (failure) {
      lastFailure = failure;
      console.error('transactionFailure', failure);
      console.error(
        'transactionFailureMessage',
        extractTransactionErrorMessage(failure)
      );
    }
  };

  const onWindowMessage = (event: MessageEvent) => onData(event?.data);
  window.addEventListener('message', onWindowMessage);

  let removeMeteorListener: (() => void) | null = null;
  try {
    const meteorCom = (window as any).meteorCom;
    if (meteorCom?.addMessageDataListener) {
      const listener = (data: any) => onData(data);
      meteorCom.addMessageDataListener(listener);
      removeMeteorListener = () => {
        try {
          meteorCom.removeMessageDataListener?.(listener);
        } catch (e) {
          // ignore
        }
      };
    }
  } catch (e) {
    // ignore
  }

  return {
    getLastFailure: () => lastFailure,
    getMessages: () => messages,
    stop: () => {
      window.removeEventListener('message', onWindowMessage);
      removeMeteorListener?.();
    }
  };
};

export const isWalletCancelError = (error: any) => {
  if (!error) return false;
  if (error instanceof WalletCancelError || error?.name === 'WalletCancelError') {
    return true;
  }
  if (extractTransactionFailure(error)) {
    return false;
  }

  const toStringValue =
    typeof error?.toString === 'function' ? error.toString() : '';
  const raw = [
    error?.name,
    error?.type,
    error?.code,
    error?.message,
    error?.cause?.message,
    error?.error?.message,
    error?.reason,
    error?.status?.Failure,
    toStringValue !== '[object Object]' ? toStringValue : '',
    stringifySafe(error)
  ]
    .filter(Boolean)
    .join(' | ')
    .toLowerCase();

  return (
    raw.includes('user closed the window') ||
    raw.includes('user rejected') ||
    raw.includes('user cancelled') ||
    raw.includes('user canceled') ||
    raw.includes('request canceled') ||
    raw.includes('request cancelled') ||
    raw.includes('rejected by user') ||
    raw.includes('cancelled by user') ||
    raw.includes('canceled by user') ||
    raw.includes('transaction cancelled') ||
    raw.includes('transaction canceled') ||
    raw.includes('action_rejected') ||
    raw.includes('user_reject') ||
    raw.includes('wallet popup closed') ||
    raw.includes('meteoractionerror')
  );
};

const resolveThrownTransactionError = (error: any, capturedFailure?: any) => {
  const failure =
    extractTransactionFailure(error) ||
    capturedFailure ||
    extractTransactionFailure(inspectWalletError(error));

  if (failure) {
    const message = extractTransactionErrorMessage(failure);
    return Object.assign(new Error(message), {
      name: 'TransactionExecutionError',
      failure,
      cause: error
    });
  }

  if (isWalletCancelError(error)) {
    return new WalletCancelError();
  }

  return error;
};

export const getWalletActionErrorPresentation = (error: any) => {
  const failure =
    error?.failure ||
    extractTransactionFailure(error) ||
    (error?.name === 'TransactionExecutionError' ? error : null);

  if (failure || error?.name === 'TransactionExecutionError') {
    const message = extractTransactionErrorMessage(failure || error);
    return {
      kind: 'execution' as const,
      messageCode: message,
      errorMessage: message
    };
  }

  if (isWalletCancelError(error)) {
    return {
      kind: 'cancel' as const,
      messageCode: 'An error occurred. Your send transaction was cancelled.',
      errorMessage: 'User closed the window'
    };
  }

  return {
    kind: 'unknown' as const,
    messageCode: error?.message || 'An error occurred.',
    errorMessage: error?.message || ''
  };
};

const blockTimestampToMs = (value: any) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (n > 1e14) return Math.floor(n / 1e6);
  if (n > 1e12) return Math.floor(n);
  return Math.floor(n * 1000);
};

const hasTransactionHashInError = (error: any) => {
  const txHash =
    error?.transactionHash ||
    error?.txHash ||
    error?.data?.txHash ||
    error?.result?.transaction?.hash ||
    error?.result?.transaction_outcome?.id;
  return typeof txHash === 'string' && txHash.length > 0;
};

// When Meteor returns "User closed the window" we still need to distinguish
// pre-sign cancel from a failed on-chain tx. Quick closes (<1s) are almost
// always popup dismiss before broadcast; longer flows may hide contract panics.
const shouldSkipOnChainLookupForCancel = (
  error: any,
  capturedFailure: any,
  startedAtMs: number
) => {
  if (capturedFailure || hasTransactionHashInError(error)) {
    return false;
  }
  if (!isWalletCancelError(error)) {
    return false;
  }
  return Date.now() - startedAtMs < 1000;
};

const fetchRecentOnChainFailure = async (
  provider: JsonRpcProvider,
  accountId: string,
  receiverId: string | undefined,
  startedAtMs: number,
  options: { maxAttempts?: number; retryDelayMs?: number } = {}
) => {
  const { maxAttempts = 3, retryDelayMs = 1200 } = options;
  const network = networkId === 'mainnet' ? 'mainnet' : 'testnet';
  const apiBase =
    network === 'mainnet'
      ? 'https://api.nearblocks.io'
      : 'https://api-testnet.nearblocks.io';
  const minTxMs = startedAtMs - 5000;

  const lookupTxStatus = async (txHash: string) => {
    try {
      if (typeof (provider as any).viewTransactionStatusWithReceipts === 'function') {
        return await (provider as any).viewTransactionStatusWithReceipts(
          txHash,
          accountId
        );
      }
      if (typeof (provider as any).txStatus === 'function') {
        return await (provider as any).txStatus(txHash, accountId);
      }
      return await provider.sendJsonRpc('EXPERIMENTAL_tx_status', [
        txHash,
        accountId
      ]);
    } catch (e) {
      return null;
    }
  };

  const lookupOnce = async () => {
    const response = await fetch(
      `${apiBase}/v1/account/${accountId}/txns?per_page=5`
    );
    if (!response.ok) return null;
    const data = await response.json();
    const txns = data?.txns || data?.data || [];
    const list = Array.isArray(txns) ? txns : [];

    const recent = list.filter((tx: any) => {
      const to = tx?.receiver_account_id || tx?.receiver_id || tx?.to;
      if (receiverId && to && to !== receiverId) return false;
      const txMs = blockTimestampToMs(
        tx?.block_timestamp ||
          tx?.block_timestamp_ms ||
          tx?.block_time ||
          tx?.timestamp
      );
      if (!txMs || txMs < minTxMs) return false;
      return true;
    });

    const hashes = recent
      .map((tx: any) => tx?.transaction_hash || tx?.hash || tx?.id || null)
      .filter(Boolean)
      .slice(0, 3);

    for (const txHash of hashes) {
      const outcome = await lookupTxStatus(txHash);
      const failure = extractTransactionFailure(outcome);
      if (failure) {
        console.error('transactionFailure', failure);
        return failure;
      }
    }
    return null;
  };

  try {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (attempt > 0 && retryDelayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
      }
      const failure = await lookupOnce();
      if (failure) return failure;
    }
    return null;
  } catch (e) {
    console.log('recentTxLookupError', e);
    return null;
  }
};

const resolveCancelMaskedOnChainFailure = async (
  provider: JsonRpcProvider,
  accountId: string,
  receiverId: string | undefined,
  startedAtMs: number,
  error: any,
  capturedFailure: any
) => {
  if (capturedFailure || !isWalletCancelError(error)) {
    return capturedFailure;
  }
  if (shouldSkipOnChainLookupForCancel(error, capturedFailure, startedAtMs)) {
    return null;
  }
  return fetchRecentOnChainFailure(
    provider,
    accountId,
    receiverId,
    startedAtMs,
    { maxAttempts: 1, retryDelayMs: 0 }
  );
};

const logTransactionOutcome = (
  label: string,
  payload: Record<string, any>
) => {
  const inspectedError = payload.error
    ? inspectWalletError(payload.error)
    : null;
  const failure =
    extractTransactionFailure(payload.result) ||
    extractTransactionFailure(payload.error) ||
    extractTransactionFailure(inspectedError) ||
    payload.capturedFailure ||
    null;

  if (failure) {
    console.error(label, {
      ...payload,
      inspectedError,
      failure,
      failureMessage: extractTransactionErrorMessage(failure)
    });
    console.error('transactionFailure', failure);
    console.error(
      'transactionFailureMessage',
      extractTransactionErrorMessage(failure)
    );
    return;
  }

  console.log(label, {
    ...payload,
    inspectedError
  });
};

export default class Wallet {
  near: NearConnection;

  selector: NearConnector;

  isSignedIn = false;

  accountId = '';

  private browserKeyStore = new BrowserLocalStorageKeyStore();

  private browserSigner = new InMemorySigner(this.browserKeyStore);

  private rpcProvider = new JsonRpcProvider({ url: NODE_URL });

  private connectorPromise: Promise<NearConnector> | null = null;

  private connectorListenersAttached = false;

  private initPromise: Promise<this> | null = null;

  private signInInFlightPromise: Promise<boolean> | null = null;

  private signQueue: Promise<unknown> = Promise.resolve();

  async initialize(options: { skipAccountRestore?: boolean } = {}) {
    if (this.initPromise && !options.skipAccountRestore) {
      return this.initPromise;
    }
    if (options.skipAccountRestore) {
      this.initPromise = null;
      this.connectorPromise = null;
      this.connectorListenersAttached = false;
    }

    this.initPromise = this.doInitialize(options);
    return this.initPromise;
  }

  private async doInitialize(options: { skipAccountRestore?: boolean } = {}) {
    this.near = {
      connection: {
        networkId,
        provider: this.rpcProvider,
        signer: this.browserSigner
      },
      account: async (accountId: string) => {
        const keyPair = await this.browserKeyStore.getKey(networkId, accountId);
        const signer = keyPair ? new KeyPairSigner(keyPair) : undefined;
        const account = new Account(accountId, this.rpcProvider, signer);
        (account as any).connection = {
          networkId,
          provider: this.rpcProvider,
          signer: this.browserSigner
        };
        (account as any).inMemorySigner = this.browserSigner;
        this.attachViewHelpers(account);
        return account;
      }
    };

    this.selector = await this.initSelector();

    if (!options.skipAccountRestore) {
      await this.hydrateActiveAccount();
    } else {
      this.accountId = '';
      this.isSignedIn = false;
    }

    return this;
  }

  private async initSelector() {
    if (!this.connectorPromise) {
      this.connectorPromise = Promise.resolve(
        new NearConnector({
          network: networkId,
          providers: {
            [networkId]: [NODE_URL]
          },
          manifest: METEOR_WALLET_MANIFEST as any,
          features: {
            signAndSendTransaction: true,
            signMessage: true
          }
        })
      );
    }

    const selector = await this.connectorPromise;
    if (!this.connectorListenersAttached) {
      selector.on('wallet:signIn', ({ accounts }: any) => {
        this.accountId = accounts?.[0]?.accountId || '';
        this.isSignedIn = !!this.accountId;
        this.rememberSessionKeys(accounts || []);
      });
      selector.on('wallet:signOut', () => {
        this.accountId = '';
        this.isSignedIn = false;
      });
      this.connectorListenersAttached = true;
    }
    return selector;
  }

  private async hydrateActiveAccount() {
    try {
      const connected = await this.selector.getConnectedWallet();
      this.accountId = connected?.accounts?.[0]?.accountId || '';
      this.isSignedIn = !!this.accountId;
      this.rememberSessionKeys(connected?.accounts || []);
    } catch (e) {
      this.accountId = '';
      this.isSignedIn = false;
    }
  }

  private syncAccountState() {
    this.isSignedIn = !!this.accountId;
  }

  private getActiveAccountId() {
    return this.accountId || null;
  }

  private async getSelectedWalletSafe() {
    try {
      return await this.selector.wallet();
    } catch (error: any) {
      if (
        error?.message?.includes('No wallet selected') ||
        error?.message?.includes('No accounts found')
      ) {
        return null;
      }
      throw error;
    }
  }

  private async getConnectedAccountIdSafe() {
    try {
      const connected = await this.selector.getConnectedWallet();
      return connected?.accounts?.[0]?.accountId || null;
    } catch (e) {
      return null;
    }
  }

  private rememberSessionKeys(accounts: any[] = []) {
    if (!Array.isArray(accounts) || !accounts.length) return;
    accounts.forEach((item) => {
      rememberSessionKeyByAccount(
        item?.accountId,
        item?.publicKey || item?.public_key || ''
      );
    });
  }

  private async resolveCurrentSessionPublicKey(accountId: string) {
    try {
      const connectedWallet = await this.selector.getConnectedWallet();
      const accounts: any[] = connectedWallet?.accounts || [];
      const target =
        accounts.find((item) => item?.accountId === accountId) || accounts[0];
      const publicKey = target?.publicKey || target?.public_key || '';
      if (publicKey) return normalizePublicKey(publicKey);
    } catch (e) {
      // continue with local signer fallback
    }

    try {
      const localPublicKey = await this.browserSigner.getPublicKey(
        accountId,
        networkId
      );
      if (localPublicKey?.toString) {
        return normalizePublicKey(localPublicKey.toString());
      }
    } catch (e) {
      // ignore
    }

    return '';
  }

  private async resolveSavedLoginSessionKey(accountId: string) {
    const rememberedKey = normalizePublicKey(
      getRememberedSessionKey(accountId) || ''
    );
    if (rememberedKey) return rememberedKey;

    const localStorageKey = resolveSessionKeyFromLocalStorage(accountId);
    if (localStorageKey) {
      rememberSessionKeyByAccount(accountId, localStorageKey);
      return localStorageKey;
    }

    let sessionPublicKey = await this.resolveCurrentSessionPublicKey(accountId);
    if (!sessionPublicKey) {
      sessionPublicKey = await resolveLikelySessionKeyFromChain(accountId);
    }
    if (sessionPublicKey) {
      rememberSessionKeyByAccount(accountId, sessionPublicKey);
      return sessionPublicKey;
    }

    return '';
  }

  private async resolveValidatedLogoutKey(accountId: string) {
    const savedKey = normalizePublicKey(
      (await this.resolveSavedLoginSessionKey(accountId)) || ''
    );
    let allKeys: any[] = [];
    try {
      allKeys = await listAccessKeysPaginated(accountId);
    } catch (e) {
      return '';
    }
    const functionCallKeySet = new Set(
      getAllFunctionCallKeys(allKeys)
        .map((item) =>
          normalizePublicKey(item?.public_key || item?.publicKey || '')
        )
        .filter(Boolean)
    );
    if (!functionCallKeySet.size) {
      if (savedKey) forgetSessionKey(accountId);
      return '';
    }

    if (savedKey && functionCallKeySet.has(savedKey)) return savedKey;
    if (savedKey) forgetSessionKey(accountId);

    const chainKey = await resolveLikelySessionKeyFromChain(accountId);
    if (chainKey && functionCallKeySet.has(chainKey)) {
      rememberSessionKeyByAccount(accountId, chainKey);
      return chainKey;
    }
    return '';
  }

  private async sendDeleteKeysWithFallback({
    walletInstance,
    fallbackAccount,
    accountId,
    batchKeys
  }: {
    walletInstance: any;
    fallbackAccount: any;
    accountId: string;
    batchKeys: string[];
  }) {
    const walletActions = batchKeys.map((publicKey) =>
      actionCreators.deleteKey(
        PublicKey.fromString(normalizePublicKey(publicKey))
      )
    );
    try {
      return await walletInstance.signAndSendTransaction({
        network: networkId,
        signerId: accountId,
        receiverId: accountId,
        actions: walletActions
      });
    } catch (error) {
      if (isIframeNotLoadedError(error)) {
        throw new Error('Iframe not loaded');
      }
      if (!isTooManyAccessKeysError(error) || !fallbackAccount) {
        throw error;
      }
      const canUseNativeFallback = await hasLocalSigningKey(
        fallbackAccount,
        accountId
      );
      if (!canUseNativeFallback) {
        throw error;
      }
      const nativeActions = batchKeys.map((publicKey) =>
        actionCreators.deleteKey(
          PublicKey.fromString(normalizePublicKey(publicKey))
        )
      );
      try {
        return await fallbackAccount.signAndSendTransaction({
          receiverId: accountId,
          actions: nativeActions
        });
      } catch (fallbackError) {
        return fallbackAccount.signAndSendTransaction(accountId, nativeActions);
      }
    }
  }

  private async tryDeleteCurrentSessionKey(accountId: string) {
    const result = {
      accountId,
      deletedKeys: 0,
      deletedPublicKey: '',
      warning: ''
    };
    if (!accountId) return result;

    const selectedWallet = await this.getSelectedWalletSafe();
    if (!selectedWallet) {
      result.warning = 'No selected wallet found for key cleanup.';
      return result;
    }

    let fallbackAccount: any = null;
    try {
      fallbackAccount = await this.near.account(accountId);
    } catch (e) {
      fallbackAccount = null;
    }

    const keyForDelete = await this.resolveValidatedLogoutKey(accountId);
    // Nothing limited to remove. Logout still finishes; the Meteor full-access key stays.
    if (!keyForDelete) return result;

    try {
      await this.sendDeleteKeysWithFallback({
        walletInstance: selectedWallet,
        fallbackAccount,
        accountId,
        batchKeys: [keyForDelete]
      });
      result.deletedKeys = 1;
      result.deletedPublicKey = keyForDelete;
      forgetSessionKey(accountId);
      console.log('walletDeletedSessionKey', {
        accountId,
        publicKey: keyForDelete
      });
    } catch (error) {
      if (isWalletCancelError(error)) {
        throw error;
      }
      result.warning = `Could not delete saved session key: ${getErrorMessage(
        error
      )}`;
    }

    return result;
  }

  private clearWalletLocalStorage() {
    try {
      const shouldRemove = (key: string) =>
        key.includes('near-api-js:keystore') ||
        key.includes('undefined_wallet_auth_key') ||
        key.includes('_wallet_auth_key') ||
        key.includes('near-wallet-selector') ||
        key.includes('wallet-selector') ||
        key.includes('meteor-wallet') ||
        key.includes('_meteor_wallet') ||
        key.startsWith('_meteor_wallet') ||
        key.includes('hot-connector') ||
        key.includes('near-connect') ||
        key.includes(SESSION_KEY_STORAGE_KEY) ||
        key.toLowerCase().includes('meteor');

      Object.keys(localStorage)
        .filter(shouldRemove)
        .forEach((key) => localStorage.removeItem(key));

      Object.keys(sessionStorage)
        .filter(shouldRemove)
        .forEach((key) => sessionStorage.removeItem(key));
    } catch (e) {
      // ignore
    }
  }

  private async silentClearSession() {
    try {
      await this.selector?.disconnect?.();
    } catch (e) {
      // ignore
    }
    this.clearWalletLocalStorage();
    this.signInInFlightPromise = null;
    this.accountId = '';
    this.isSignedIn = false;
    this.connectorPromise = null;
    this.connectorListenersAttached = false;
    this.initPromise = null;
    await this.initialize({ skipAccountRestore: true });
  }

  private async waitForActiveAccount(
    timeoutMs = 15000,
    intervalMs = 100,
    expectedAccountId = ''
  ) {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      const current = this.getActiveAccountId();
      if (current && (!expectedAccountId || current === expectedAccountId)) {
        return true;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
    const current = this.getActiveAccountId();
    return !!(current && (!expectedAccountId || current === expectedAccountId));
  }

  private attachViewHelpers(account: Account) {
    const nativeViewFunction = account.viewFunction.bind(account);
    (account as any).viewFunction = async (...input: any[]) => {
      if (
        input.length === 1 &&
        typeof input[0] === 'object' &&
        input[0] !== null
      ) {
        return nativeViewFunction(input[0]);
      }
      const [contractId, methodName, args = {}] = input;
      try {
        return await nativeViewFunction({
          contractId,
          methodName,
          args
        });
      } catch (e) {
        return nativeViewFunction(contractId, methodName, args);
      }
    };
    (account as any).viewFunctionV1 = async (
      contractId: string,
      methodName: string,
      args: object = {}
    ) =>
      (account as any).viewFunction({
        contractId,
        methodName,
        args
      });
    return account;
  }

  async requestSignIn(options: { forceConnect?: boolean } = {}) {
    const { forceConnect = false } = options;

    if (!forceConnect) {
      await this.initialize();
      if (this.getActiveAccountId()) {
        this.syncAccountState();
        return true;
      }
    } else {
      await this.silentClearSession();
    }

    if (this.signInInFlightPromise && !forceConnect) {
      return this.signInInFlightPromise;
    }

    this.signInInFlightPromise = (async () => {
      try {
        if (forceConnect) {
          this.accountId = '';
          this.isSignedIn = false;
          await this.selector.disconnect().catch(() => {});
        }

        await this.selector.connect({ walletId: METEOR_WALLET_ID });
        const connectedWallet = await this.selector.getConnectedWallet();
        const connectedAccountId =
          connectedWallet?.accounts?.[0]?.accountId || null;
        this.accountId = connectedAccountId || '';
        this.isSignedIn = !!this.accountId;
        this.rememberSessionKeys(connectedWallet?.accounts || []);
        return await this.waitForActiveAccount(
          15000,
          100,
          connectedAccountId || ''
        );
      } catch (error) {
        if (isWalletCancelError(error)) {
          this.syncAccountState();
          return !!this.getActiveAccountId();
        }
        console.log('walletRequestSignInError', error);
        return false;
      } finally {
        this.signInInFlightPromise = null;
      }
    })();

    return this.signInInFlightPromise;
  }

  async signOut() {
    await this.initialize();
    try {
      const connectedAccountId = await this.getConnectedAccountIdSafe();
      const accountId = connectedAccountId || this.getActiveAccountId();
      if (!accountId) {
        this.syncAccountState();
        return true;
      }

      // Delete the login session key on-chain first (same as dashboard / ofp-admin).
      // Meteor will prompt to approve DeleteKey; only then clear the local session.
      const maintenanceResult = await this.tryDeleteCurrentSessionKey(accountId);
      if (maintenanceResult?.warning) {
        console.log(
          'walletDeleteKeyOnLogoutWarning',
          maintenanceResult.warning
        );
        return false;
      }

      // Silent local logout after key deleted — no extra Meteor signOut popup.
      this.accountId = '';
      this.isSignedIn = false;
      this.clearWalletLocalStorage();
      this.syncAccountState();
      return true;
    } catch (error) {
      if (isWalletCancelError(error)) {
        return false;
      }
      console.log('walletSignOutError', error);
      return false;
    }
  }

  private async signAndSendTransactionWithWallet(
    accountId: string,
    { receiverId, actions }: SignAndSendTransactionOptions
  ) {
    const run = async () => {
      await this.initialize();
      const selectedWallet = await this.getSelectedWalletSafe();
      if (!selectedWallet) {
        throw new Error('No wallet selected');
      }

      const meteorCapture = captureMeteorMessages();
      const startedAtMs = Date.now();
      try {
        const result = await selectedWallet.signAndSendTransaction({
          network: networkId,
          signerId: accountId,
          receiverId,
          actions: actions.map(convertNearActionToSelectorAction)
        });
        logTransactionOutcome('transactionResult', {
          receiverId,
          accountId,
          result,
          capturedFailure: meteorCapture.getLastFailure()
        });
        const failure =
          extractTransactionFailure(result) || meteorCapture.getLastFailure();
        if (failure) {
          throw Object.assign(
            new Error(extractTransactionErrorMessage(failure)),
            {
              name: 'TransactionExecutionError',
              failure
            }
          );
        }
        return result;
      } catch (error) {
        let capturedFailure = meteorCapture.getLastFailure();
        capturedFailure = await resolveCancelMaskedOnChainFailure(
          this.rpcProvider,
          accountId,
          receiverId,
          startedAtMs,
          error,
          capturedFailure
        );
        logTransactionOutcome('transactionError', {
          receiverId,
          accountId,
          error,
          capturedFailure,
          meteorMessages: meteorCapture.getMessages(),
          inspectedError: inspectWalletError(error),
          message: extractTransactionErrorMessage(capturedFailure || error)
        });
        throw resolveThrownTransactionError(error, capturedFailure);
      } finally {
        meteorCapture.stop();
      }
    };

    const result = this.signQueue.then(run, run);
    this.signQueue = result.then(
      () => undefined,
      () => undefined
    );
    return result;
  }

  async signAndSendTransactions(transactionsList: WalletTransaction[]) {
    if (!transactionsList?.length) {
      return [];
    }

    const run = async () => {
      await this.initialize();
      const accountId =
        (await this.getConnectedAccountIdSafe()) || this.getActiveAccountId();
      if (!accountId) {
        throw new Error('No wallet selected');
      }
      this.accountId = accountId;
      this.isSignedIn = true;

      const selectedWallet = await this.getSelectedWalletSafe();
      if (!selectedWallet) {
        throw new Error('No wallet selected');
      }

      const meteorCapture = captureMeteorMessages();
      const startedAtMs = Date.now();
      try {
        const result = await selectedWallet.signAndSendTransactions({
          network: networkId,
          transactions: transactionsList.map((tx) => ({
            signerId: accountId,
            receiverId: tx.receiverId,
            actions: tx.actions
          }))
        });
        logTransactionOutcome('transactionsResult', {
          accountId,
          receivers: transactionsList.map((tx) => tx.receiverId),
          result,
          capturedFailure: meteorCapture.getLastFailure()
        });
        const failure =
          extractTransactionFailure(result) || meteorCapture.getLastFailure();
        if (failure) {
          throw Object.assign(
            new Error(extractTransactionErrorMessage(failure)),
            {
              name: 'TransactionExecutionError',
              failure
            }
          );
        }
        return result;
      } catch (error) {
        let capturedFailure = meteorCapture.getLastFailure();
        const primaryReceiver = transactionsList[0]?.receiverId;
        capturedFailure = await resolveCancelMaskedOnChainFailure(
          this.rpcProvider,
          accountId,
          primaryReceiver,
          startedAtMs,
          error,
          capturedFailure
        );
        logTransactionOutcome('transactionsError', {
          accountId,
          receivers: transactionsList.map((tx) => tx.receiverId),
          error,
          capturedFailure,
          meteorMessages: meteorCapture.getMessages(),
          inspectedError: inspectWalletError(error),
          message: extractTransactionErrorMessage(capturedFailure || error)
        });
        throw resolveThrownTransactionError(error, capturedFailure);
      } finally {
        meteorCapture.stop();
      }
    };

    const result = this.signQueue.then(run, run);
    this.signQueue = result.then(
      () => undefined,
      () => undefined
    );
    return result;
  }

  private attachWalletSigning(account: Account, accountId: string) {
    this.attachViewHelpers(account);

    (account as any).signAndSendTransaction = async (
      signAndSendTransactionOptions: SignAndSendTransactionOptions
    ) =>
      this.signAndSendTransactionWithWallet(
        accountId,
        signAndSendTransactionOptions
      );

    (account as any).sendMoney = async (
      receiverId: string,
      amount: string | BN
    ) =>
      this.signAndSendTransactionWithWallet(accountId, {
        receiverId,
        actions: [actionCreators.transfer(toBigInt(amount))]
      });

    (account as any).functionCall = async (...input: any[]) => {
      const normalized =
        input.length === 1 && typeof input[0] === 'object' && input[0] !== null
          ? input[0]
          : {
              contractId: input[0],
              methodName: input[1],
              args: input[2] || {},
              gas: input[3],
              attachedDeposit: input[4]
            };

      const {
        contractId,
        methodName,
        args = {},
        gas,
        attachedDeposit,
        deposit
      } = normalized;

      return this.signAndSendTransactionWithWallet(accountId, {
        receiverId: contractId,
        actions: [
          createFunctionCallAction(
            methodName,
            args,
            gas || '30000000000000',
            attachedDeposit || deposit || '0'
          )
        ]
      });
    };

    return account;
  }

  get walletConnection() {
    return {
      getAccountId: () => this.accountId,
      isSignedIn: () => this.isSignedIn,
      isSignedInAsync: async () => {
        await this.initialize();
        return this.isSignedIn;
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      requestSignIn: async (_options?: any) => this.requestSignIn(),
      signOut: async () => this.signOut()
    };
  }

  async getAccount(accountId: string, limitedAccountData = false) {
    await this.initialize();
    const account = this.attachWalletSigning(
      await this.near.account(accountId),
      accountId
    );

    if (limitedAccountData) {
      return account;
    }

    return decorateWithLockup(account);
  }

  async getAccountBasic(accountId: string) {
    await this.initialize();
    return this.attachViewHelpers(await this.near.account(accountId));
  }

  async loadAccount() {
    const account = await this.getAccount(this.accountId);
    const state = await account.state();
    return {
      ...state,
      balance: {
        available: '',
        balanceAvailable: ''
      },
      accountId: this.accountId
    };
  }

  async getBalance(accountId?: string) {
    accountId = accountId || this.accountId;

    if (!accountId) {
      return false;
    }
    const account = await this.getAccount(accountId);
    return await account.getAccountBalance();
  }

  isLegitAccountId(accountId: string) {
    return ACCOUNT_ID_REGEX.test(accountId);
  }

  async checkAccountAvailable(accountId: string) {
    if (!this.isLegitAccountId(accountId)) {
      throw new Error('Invalid username.');
    }

    return await (await this.getAccount(accountId)).state();
  }
}

export const wallet = new Wallet();
wallet.initialize();
